import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LogIn, Undo2 } from "lucide-react";
import { useSession } from "../../hooks/useSession";
import { customersAdminApi } from "../../api/customersAdminApi";
import { useBrand } from "../brand/BrandProvider";
import { BrandLogo } from "./BrandLogo";

/** sessionStorage key: path to return to after "Switch back" (set by pages that impersonate). */
export const IMPERSONATE_RETURN_KEY = "aivrmanager.impersonate.returnTo";

export function TopBar() {
  const { session, setSession } = useSession();
  const { brand, refresh: refreshBrand } = useBrand();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  // An admin or manager is currently impersonating a customer when the
  // session remembers their identity in `originalKind`. We use that flag
  // to render a distinct banner with a one-click "Switch back".
  const isImpersonating =
    session?.isAuthenticated &&
    (session.originalKind === "Admin" || session.originalKind === "Manager");

  // TopBar is mounted once for the lifetime of the SPA, so it survives
  // every navigation between admin and impersonated-customer screens.
  // That means a stale `exiting=true` from a previous Switch-back would
  // stick around and disable the button on the *next* impersonation
  // until the page is hard-reloaded. Reset it whenever a new
  // impersonation banner appears so the user always lands on an
  // enabled button.
  useEffect(() => {
    if (isImpersonating) setExiting(false);
  }, [isImpersonating]);

  const handleSwitchBack = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      const restored = await customersAdminApi.exitImpersonate();
      setSession(restored?.isAuthenticated ? restored : null);
      try {
        await refreshBrand();
      } catch {
        // brand refresh is cosmetic
      }
      // A page that started the impersonation (e.g. IPPBX → Extensions) can
      // ask to be returned to; the customers list stays the default.
      let returnTo: string | null = null;
      try {
        returnTo = sessionStorage.getItem(IMPERSONATE_RETURN_KEY);
        sessionStorage.removeItem(IMPERSONATE_RETURN_KEY);
      } catch {
        // storage unavailable
      }
      navigate(returnTo && returnTo.startsWith("/") ? returnTo : "/admin/customers");
    } finally {
      // Always release the button, even on the happy path. Because
      // TopBar is not unmounted on navigate, leaving it stuck on
      // `true` would silently disable the next Switch-back.
      setExiting(false);
    }
  };

  return (
    <header className="topbar">
      <Link to="/" className="brand">
        <span className="brand-mark" aria-hidden="true">
          <BrandLogo />
        </span>
        {brand.portalName ? (
          <span className="brand-name">{brand.portalName}</span>
        ) : null}
      </Link>
      <nav style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {isImpersonating && (
          <div
            role="region"
            aria-label={`Viewing the portal as ${session?.displayName ?? "customer"}. Switch back to return to admin session.`}
            className="impersonation-banner"
          >
            <span>{session?.originalDisplayName?.trim() || "Admin"}</span>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleSwitchBack}
              disabled={exiting}
            >
              <Undo2 size={12} aria-hidden="true" />
              {exiting ? "Switching back…" : "Switch back"}
            </button>
          </div>
        )}
        {session?.isAuthenticated ? null : (
          <Link to="/login" className="btn btn-primary">
            <LogIn size={16} /> Sign in
          </Link>
        )}
      </nav>
    </header>
  );
}
